/**
 * Org Debug (DEV-only)
 *
 * URL: /dev/org-debug
 *
 * Proves client-side RLS reads for organizations / memberships.
 * No writes, no flags, no competitive engine interaction.
 */

import { View, Text, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganizations } from '@/hooks/useOrganizations';

export default function OrgDebugScreen() {
  const { user } = useAuth();
  const { memberships, organizations, loading, getPrimaryOrg } = useOrganizations();

  if (!__DEV__) {
    return (
      <View style={styles.container}>
        <Text style={styles.muted}>Not available.</Text>
      </View>
    );
  }

  if (loading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="small" />
        <Text style={styles.muted}>Loading…</Text>
      </View>
    );
  }

  const primaryGym = getPrimaryOrg('gym');

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.heading}>Org Debug</Text>

      <Text style={styles.label}>User ID</Text>
      <Text style={styles.value}>{user?.id ?? '—'}</Text>

      <Text style={styles.label}>Membership count</Text>
      <Text style={styles.value}>{memberships.length}</Text>

      <Text style={styles.label}>Primary gym</Text>
      <Text style={styles.value}>
        {primaryGym ? `${primaryGym.name} (${primaryGym.id})` : 'None'}
      </Text>

      <Text style={[styles.label, { marginTop: 16 }]}>Memberships</Text>
      {memberships.length === 0 && <Text style={styles.muted}>No memberships found.</Text>}
      {memberships.map((m) => (
        <View key={m.id} style={styles.row}>
          <Text style={styles.mono}>org_id: {m.org_id}</Text>
          <Text style={styles.mono}>role: {m.role}</Text>
          <Text style={styles.mono}>primary: {String(m.is_primary_for_type)}</Text>
          <Text style={styles.mono}>status: {m.status}</Text>
        </View>
      ))}

      {organizations.length > 0 && (
        <>
          <Text style={[styles.label, { marginTop: 16 }]}>Organizations</Text>
          {organizations.map((o) => (
            <View key={o.id} style={styles.row}>
              <Text style={styles.mono}>{o.name} ({o.id})</Text>
              <Text style={styles.mono}>type: {o.org_type}</Text>
            </View>
          ))}
        </>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#111' },
  content: { padding: 16 },
  heading: { color: '#fff', fontSize: 18, fontWeight: '700', marginBottom: 12 },
  label: { color: '#888', fontSize: 12, marginTop: 8 },
  value: { color: '#fff', fontSize: 14, marginBottom: 4 },
  muted: { color: '#666', fontSize: 13 },
  row: {
    backgroundColor: '#1a1a1a',
    borderRadius: 4,
    padding: 10,
    marginTop: 6,
  },
  mono: { color: '#ccc', fontSize: 12, fontFamily: 'monospace' },
});
